import { RiCalendar2Fill } from '@remixicon/react';
import { useT } from '@transifex/react';
import { useId, useRef, useState } from 'react';
import type { ComponentProps, ReactNode } from 'react';

import { Button } from '@playbook/atoms/button';
import { FormLabel } from '@playbook/atoms/form-label';
import { DropdownTrigger } from '@playbook/atoms/inputs/dropdown-trigger';
import type { DropdownTriggerSize } from '@playbook/atoms/inputs/dropdown-trigger';
import { Calendar } from '@playbook/modules/calendar';
import type {
	CalendarRange,
	CalendarWeekStart,
} from '@playbook/modules/calendar';
import { cv } from '@playbook/utils';
import { useAnchorPosition } from '@playbook/utils/anchor';
import type { AnchorPlacement } from '@playbook/utils/anchor';

import css from './styles.module.css';

export type DatePickerType = 'single' | 'range';

/**
 *	`overlay` covers the field, as Figma draws it. `bottomRight` opens below
 *	it with the right edges aligned, for a picker near the right of the screen.
 */
export type DatePickerAppearFrom = Extract<
	AnchorPlacement,
	'overlay' | 'bottomRight'
>;

const DefaultDatePickerAppearFrom: DatePickerAppearFrom = 'overlay' as const;

const DefaultDatePickerSize: DropdownTriggerSize = 'md' as const;

/**
 *	Typed for the element they land on. `{...props}` goes to the trigger, not
 *	to the wrapper, as on `modules/dropdown`.
 */
type DatePickerEventHandlers = {
	[K in keyof ComponentProps<'button'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'button'>[K];
};

interface DatePickerDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface DatePickerCommonProperties
	extends DatePickerEventHandlers, DatePickerDataAttributes {
	size?: DropdownTriggerSize;
	appearFrom?: DatePickerAppearFrom;
	disabled?: boolean;
	required?: boolean;
	optional?: boolean;

	weekStartsOn?: CalendarWeekStart;

	/** Month names and the date in the trigger come from `Intl`, in this. */
	locale?: string;

	/** The day drawn as today. Left out, it is today. */
	today?: string;

	name: string;

	/** Shown until a date is picked. What to pick is the caller's to say. */
	placeholder: string;
	previousLabel?: string;
	nextLabel?: string;
	todayLabel?: string;

	label?: string;
	description?: string;
	help?: string;
}

interface DatePickerSingleProperties extends DatePickerCommonProperties {
	type?: 'single';
	value?: string;
	onValueChange: (value: string) => void;
}

interface DatePickerRangeProperties extends DatePickerCommonProperties {
	type: 'range';
	value?: CalendarRange;
	onValueChange: (value: CalendarRange) => void;
}

type DatePickerProperties =
	| DatePickerSingleProperties
	| DatePickerRangeProperties;

function fromIso(iso: string): Date {
	const [year = 0, month = 1, day = 1] = iso.split('-').map(Number);

	return new Date(year, month - 1, day, 12);
}

/**
 *	A labelled field that opens a calendar and takes a date, or a range of
 *	them, from it.
 *
 *	The field is `atoms/inputs/dropdown-trigger` with a calendar in front, since Figma
 *	draws both from the same `.text input`. The calendar is
 *	`modules/calendar`. This arranges the two and owns what neither can:
 *	whether the calendar is showing, and when to put it away. Holding that is
 *	what makes it a module rather than a composition.
 *
 *	Not a native `<input type="date">`. Its popup cannot be styled, so Figma's
 *	`state=active` could not be drawn at all, and it has no ranges.
 *
 *	Opening is the platform's, as on `modules/context-menu`: the calendar is a
 *	`popover="auto"`, so Escape, a click outside and focus return all come
 *	from the browser. It closes itself on the press that finishes a choice —
 *	one day, or a range's second end.
 */
export function DatePicker(properties: DatePickerProperties): ReactNode {
	const {
		type = 'single',
		size = DefaultDatePickerSize,
		appearFrom = DefaultDatePickerAppearFrom,
		disabled = false,
		required = false,
		optional = false,

		weekStartsOn,
		locale,
		today,

		name,
		value,
		onValueChange,

		placeholder,
		previousLabel,
		nextLabel,
		todayLabel,

		label,
		description,
		help,

		...props
	} = properties;

	const t = useT();
	const todayText: string = todayLabel ?? t('Today');

	/** Stripped of colons for `anchor-name`, as in `modules/context-menu`. */
	const instance: string = useId().replaceAll(':', '');
	const popover: string = `${instance}-calendar`;
	const surface = useRef<HTMLDivElement>(null);
	const trigger = useRef<HTMLSpanElement>(null);

	/** Mirrored for `aria-expanded`, which the platform does not write. */
	const [open, setOpen] = useState<boolean>(false);

	const anchor = cv({ anchor: `--${instance}` });

	useAnchorPosition(trigger, surface, true, { placement: appearFrom });

	/**
	 *	One formatter for both kinds. `formatRange` is what makes a range read
	 *	the way the locale reads one — "October 26 – November 4, 2026" in
	 *	English — rather than two dates with a dash glued between them.
	 */
	const format = new Intl.DateTimeFormat(locale, { dateStyle: 'long' });

	let shown: string | undefined;
	let submitted: string = '';

	if (properties.type === 'range') {
		const range: CalendarRange | undefined = properties.value;

		if (range?.end) {
			shown = format.formatRange(fromIso(range.start), fromIso(range.end));
			submitted = `${range.start}/${range.end}`;
		} else if (range) {
			shown = format.format(fromIso(range.start));
		}
	} else if (properties.value) {
		shown = format.format(fromIso(properties.value));
		submitted = properties.value;
	}

	function close(): void {
		surface.current?.hidePopover();
	}

	/**
	 *	The two kinds, handed to the calendar with the close each one needs. A
	 *	range stays open after its first end, since it is only half picked.
	 */
	const calendar: ReactNode =
		properties.type === 'range' ? (
			<Calendar
				type="range"
				weekStartsOn={weekStartsOn}
				locale={locale}
				today={today}
				value={properties.value}
				previousLabel={previousLabel}
				nextLabel={nextLabel}
				onValueChange={(next) => {
					properties.onValueChange(next);

					if (next.end) {
						close();
					}
				}}
				bottomBar={
					<Button
						size="sm"
						label={todayText}
						onClick={() =>
							properties.onValueChange({
								start: today ?? todayIso(),
							})
						}
					/>
				}
			/>
		) : (
			<Calendar
				type="default"
				weekStartsOn={weekStartsOn}
				locale={locale}
				today={today}
				value={properties.value}
				previousLabel={previousLabel}
				nextLabel={nextLabel}
				onValueChange={(next: string) => {
					properties.onValueChange(next);
					close();
				}}
				bottomBar={
					<Button
						size="sm"
						label={todayText}
						onClick={() => {
							properties.onValueChange(today ?? todayIso());
							close();
						}}
					/>
				}
			/>
		);

	return (
		<div data-type={type} data-size={size} className={css.field}>
			{label && (
				<FormLabel
					htmlFor={name}
					size={size}
					label={label}
					description={description}
					help={help}
					optional={optional}
					required={required}
				/>
			)}

			<span ref={trigger} className={css.anchor} style={anchor}>
				{/**
				 *	Named by the label, then by its own contents, so it is
				 *	announced as "Pick a date, 14 September 2026". See
				 *	`modules/dropdown` for why `htmlFor` alone does not do it.
				 */}
				<DropdownTrigger
					{...props}
					size={size}
					disabled={disabled}
					name={name}
					placeholder={placeholder}
					value={shown}
					leadingIcon={<RiCalendar2Fill />}
					popoverTarget={popover}
					aria-haspopup="dialog"
					aria-expanded={open}
					aria-labelledby={label ? `${name}-label ${name}` : undefined}
					aria-describedby={description ? `${name}-description` : undefined}
				/>

				<div
					ref={surface}
					id={popover}
					popover="auto"
					role="dialog"
					aria-label={label ?? placeholder}
					style={anchor}
					data-appear-from={appearFrom}
					className={css.popover}
					onToggle={(event) => setOpen(event.newState === 'open')}>
					{calendar}
				</div>
			</span>

			{/**
			 *	What a form submits: the day as `YYYY-MM-DD`, or a finished range
			 *	as ISO 8601's `start/end`. A range with one end is not a value yet,
			 *	so it submits nothing.
			 */}
			<input type="hidden" name={name} value={submitted} />
		</div>
	);
}

function todayIso(): string {
	const now: Date = new Date();
	const month: string = String(now.getMonth() + 1).padStart(2, '0');
	const day: string = String(now.getDate()).padStart(2, '0');

	return `${now.getFullYear()}-${month}-${day}`;
}
